import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Paper, Chip, Stack, Divider,
  Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Tooltip, CircularProgress, Button, Avatar,
  useMediaQuery, TextField, MenuItem, InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  DirectionsCar as CarIcon,
  CheckCircleOutline as OkIcon,
  TaskAlt as ActionIcon,
  CompressOutlined as PressureIcon,
  DeviceThermostat as TempIcon,
  SensorsOff as SensorIcon,
  WifiOff as ConnIcon,
  Balance as BalanceIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  DoneAll as ResolveIcon,
  RemoveRedEye as SeenIcon,
  AddTask as AddTaskIcon,
  OpenInNew as OpenIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';
import VehicleSchematic from '../components/vehicles/detail/VehicleSchematic';
import { printSection, printTimestamp } from '../utils/print';
import { useAuth } from '../hooks/useAuth';

const tFallback = (_key, def) => def;

// Filtros rápidos por tipo de incidencia (atajos sobre los filtros detallados).
const QUICK_FILTERS = [
  { key: 'critical', label: i18n.t('hardcoded.pages_tpmsactionsummarypage.solo_criticos') },
  { key: 'no_report', label: i18n.t('hardcoded.pages_tpmsactionsummarypage.solo_sin_reporte') },
  { key: 'dual', label: i18n.t('hardcoded.pages_tpmsactionsummarypage.solo_dual') },
  { key: 'pressure', label: i18n.t('hardcoded.pages_tpmsactionsummarypage.solo_ajustes_de_presion') },
];

// Plan del equipo completo: mismos ajustes (▲/▼ a la presión ideal en frío) que
// se envían por correo. El backend los calcula en `tpms_action_plan`; aquí solo se pintan.
const PLAN_COLORS = {
  subir: 'warning.dark',
  bajar: 'info.dark',
  ok: 'success.dark',
  sin_reporte: 'text.secondary',
  sin_sensor: 'text.disabled',
};

// Aviso cuando el cierre automatico se bloqueo porque la mejora de presion venia
// de la temperatura y no de una calibracion real (el ticket sigue abierto).
const ResolutionBlockNote = ({ block }) => {
  if (!block) return null;
  const detail = [
    block.estimated_cold_psi != null ? `frio estimado ${block.estimated_cold_psi} PSI` : null,
    block.ideal_psi != null ? `ideal ${block.ideal_psi} PSI` : null,
  ].filter(Boolean).join(' · ');
  return (
    <Box sx={{ mt: 0.5, px: 1, py: 0.5, borderLeft: '3px solid', borderColor: 'warning.main', bgcolor: 'warning.50', borderRadius: 0.5 }}>
      <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark', fontWeight: 600 }}>
        {block.warning}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        {block.action}{detail ? ` (${detail})` : ''}
      </Typography>
    </Box>
  );
};

const FleetPlanPanel = ({ plan, highlight = [] }) => {
  const { t } = useTranslation();
  if (!plan?.positions?.length) return null;
  const ideals = Object.entries(plan.ideal_by_axle || {});
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {t('hardcoded.pages_tpmsactionsummarypage.plan_del_equipo')}</Typography>
      {ideals.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
          {t('hardcoded.pages_tpmsactionsummarypage.presion_ideal_en_frio')}{ideals.map(([axle, val]) => t('hardcoded.pages_tpmsactionsummarypage.eje_axle_val_psi', { axle: axle, val: val })).join(' · ')}
        </Typography>
      )}
      <Stack spacing={0.25}>
        {plan.positions.map((p) => (
          <Stack key={p.position} direction="row" spacing={1} alignItems="baseline">
            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 46 }}>
              {i18n.t('hardcoded.components_tickets_tickettriggercalculation.pos')}{p.position}
            </Typography>
            <Typography variant="caption" sx={{ color: PLAN_COLORS[p.status] || 'text.primary', fontWeight: highlight.includes(p.position) ? 700 : 500 }}>
              {p.status === 'subir' && `▲ ${t('hardcoded.pages_tpmsactionsummarypage.subir')} ${p.delta} PSI`}
              {p.status === 'bajar' && `▼ ${t('hardcoded.pages_tpmsactionsummarypage.bajar')} ${p.delta} PSI`}
              {p.status === 'ok' && t('hardcoded.pages_tpmsactionsummarypage.ok_mantener')}
              {p.status === 'sin_reporte' && t('hardcoded.pages_tpmsactionsummarypage.sin_reportepidealcalibrara', { pidealcalibrara: p.ideal ? ` — calibrar a ${p.ideal} PSI` : '' })}
              {p.status === 'sin_sensor' && t('hardcoded.pages_tpmsactionsummarypage.sin_sensor_asignado')}
              {p.stale && ['subir', 'bajar', 'ok'].includes(p.status) && t('hardcoded.pages_tpmsactionsummarypage.lectura_no_reciente')}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
        {t('hardcoded.pages_tpmsactionsummarypage.valores_actuales')}
      </Typography>
    </Box>
  );
};

const PressuresPanel = ({ items }) => {
  const { t } = useTranslation();
  const rows = (items || []).filter(
    (it) => it.current_pressure != null || it.ideal_pressure != null || it.temperature != null,
  );
  if (!rows.length) return null;
  const unit = (it) => it.unit || 'PSI';
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {t('hardcoded.pages_tpmsactionsummarypage.presiones')}</Typography>
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {rows.map((it) => {
          const dual = (it.group_positions || []).length >= 2;
          return (
            <Box key={it.ticket_id} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {it.location_label}{dual ? t('hardcoded.pages_tpmsactionsummarypage.dual_pos_itgroup_positio', { itgroup_positio: it.group_positions.join(' y ') }) : ''}
              </Typography>
              {it.current_pressure != null && (
                <Typography variant="body2">
                  {t('hardcoded.pages_tpmsactionsummarypage.presion_al_disparo')}<strong>{it.current_pressure}{` ${unit(it)}`}</strong>
                </Typography>
              )}
              {it.ideal_pressure != null && (
                <Typography variant="body2" sx={{ color: 'success.dark' }}>
                  {t('hardcoded.pages_tpmsactionsummarypage.presion_ideal')}<strong>{it.ideal_pressure}{` ${unit(it)}`}</strong>
                  {it.deficit ? t('hardcoded.pages_tpmsactionsummarypage.faltan_deficit', { deficit: it.deficit }) : it.excess ? t('hardcoded.pages_tpmsactionsummarypage.sobran_excess', { excess: it.excess }) : ''}
                </Typography>
              )}
              {it.psi_cold != null && (
                <Typography variant="body2" color="text.secondary">
                  {t('hardcoded.pages_tpmsactionsummarypage.presion_en_frio_actual')}<strong>{it.psi_cold} PSI</strong>
                  {it.cold_source === 'estimado' ? t('hardcoded.pages_tpmsactionsummarypage.estimada') : ''}
                </Typography>
              )}
              {it.temperature != null && (
                <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                  {t('hardcoded.pages_tpmsactionsummarypage.temperatura_al_disparo')}<strong>{it.temperature} °C</strong>
                </Typography>
              )}
              {it.plan_action && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'primary.main', fontWeight: 600 }}>
                  ➤ {it.plan_action}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

const SEVERITY_COLOR = {
  extreme: { bg: '#7f0000', label: i18n.t('alarmTickets.severity.extreme') },
  critical: { bg: '#d32f2f', label: i18n.t('alarmTickets.severity.critical') },
  alarm: { bg: '#ed6c02', label: i18n.t('alarmTickets.severity.alarm') },
};
const CATEGORY_ICON = {
  [i18n.t('hardcoded.components_alarmprofiles_profilecard.presion')]: <PressureIcon fontSize="small" />,
  [i18n.t('hardcoded.components_alarmprofiles_profilecard.temperatura')]: <TempIcon fontSize="small" />,
  [i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.sensor')]: <SensorIcon fontSize="small" />,
  [i18n.t('vehicleDetail.tabs.connectivity')]: <ConnIcon fontSize="small" />,
  [i18n.t('hardcoded.pages_tpmsactionsummarypage.balance_dual')]: <BalanceIcon fontSize="small" />,
};
const sevColor = (sev) => (SEVERITY_COLOR[sev] || SEVERITY_COLOR.alarm);

const KpiCard = ({ label, value, color = '#1a1a1a', sub }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
    <CardContent sx={{ py: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1.1, mt: 0.5 }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

const TPMSActionSummaryPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Filtros
  const [fSeverity, setFSeverity] = useState('all');
  const [fCategory, setFCategory] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [search, setSearch] = useState('');
  // Filtro rápido por tipo de incidencia (excluyente con el resto de filtros).
  const [quick, setQuick] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    api.systemUsage.track('reports', 'opened', { feature: t('common.actions'), path: '/reports/acciones' });
    try {
      const res = await api.reports.getActionSummary();
      setData(res?.data || null);
    } catch (e) {
      showError(e?.response?.data?.detail || t('hardcoded.pages_tpmsactionsummarypage.error_cargando_el_resumen_de'));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const bySev = totals.by_severity || {};
  const generatedAt = data?.generated_at ? new Date(data.generated_at) : null;
  const categoryOptions = Object.keys(totals.by_category || {});

  // Aplicar filtros a nivel de ticket y descartar vehículos sin coincidencias.
  const vehicles = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.vehicles || []).map((v) => {
      const matchesQuick = (it) => {
        switch (quick) {
          case 'critical': return it.severity === 'critical' || it.severity === 'extreme';
          case 'no_report': return it.metric === 'NO_REPORT' || it.category === 'Sensor' || it.category === t('vehicleDetail.tabs.connectivity');
          case t('hardcoded.components_alarmprofiles_profileform.dual'): return (it.group_positions || []).length >= 2 || it.category === 'Balance dual';
          case 'pressure': return it.category === t('hardcoded.components_alarmprofiles_profilecard.presion');
          default: return true;
        }
      };
      const items = (v.items || []).filter((it) => (
        (fSeverity === 'all' || it.severity === fSeverity)
        && (fCategory === 'all' || it.category === fCategory)
        && (fStatus === 'all'
          || (fStatus === 'acknowledged' ? it.status === 'acknowledged' : it.status !== 'acknowledged'))
        && matchesQuick(it)
      ));
      return { ...v, items };
    }).filter((v) => v.items.length > 0 && (
      !q || (v.vehicle_name || '').toLowerCase().includes(q)
      || v.items.some((it) => (it.ticket_number || '').toLowerCase().includes(q))
    ));
  }, [data, fSeverity, fCategory, fStatus, search, quick]);

  const shownCount = vehicles.reduce((n, v) => n + v.items.length, 0);

  // --- Acciones ---
  const doAck = async (it) => {
    setBusyId(it.ticket_id);
    try { await api.alarmTickets.acknowledge(it.ticket_id); showSuccess(t('hardcoded.components_schematics_vehicleticketsdrawer.ticket_marcado_como_visto')); await load(); }
    catch (e) { showError(e?.response?.data?.detail || t('hardcoded.pages_tpmsactionsummarypage.no_se_pudo_marcar_como')); }
    finally { setBusyId(null); }
  };
  const doResolve = async (it) => {
    if (!window.confirm(t('hardcoded.pages_tpmsactionsummarypage.resolver_el_ticket_ticket_number', { ticket_number: it.ticket_number }))) return;
    setBusyId(it.ticket_id);
    try { await api.alarmTickets.resolve(it.ticket_id, t('hardcoded.pages_tpmsactionsummarypage.resuelto_desde_resumen_de_acciones')); showSuccess(t('hardcoded.components_schematics_vehicleticketsdrawer.ticket_resuelto')); await load(); }
    catch (e) { showError(e?.response?.data?.detail || t('hardcoded.pages_tpmsactionsummarypage.no_se_pudo_resolver')); }
    finally { setBusyId(null); }
  };
  const doCreateTask = async (it, v) => {
    setBusyId(it.ticket_id);
    try {
      await api.tasks.create({
        title: t('hardcoded.pages_tpmsactionsummarypage.revisar_metric_label_vehicle_name_location_label', { metric_label: it.metric_label, vehicle_name: v.vehicle_name, location_label: it.location_label }),
        description: it.action || '',
        priority: (it.severity === 'extreme' || it.severity === 'critical') ? 'high' : 'medium',
        ...(v.vehicle_id && v.vehicle_id !== '—' ? { vehicle_id: v.vehicle_id } : {}),
        ...(it.ticket_id ? { ticket_id: it.ticket_id } : {}),
        ...(it.position ? { position: it.position } : {}),
      });
      showSuccess(t('hardcoded.components_tickets_ticketactioncenter.tarea_creada'));
    } catch (e) { showError(e?.response?.data?.detail || t('hardcoded.components_tickets_ticketaianalysiscard.no_se_pudo_crear_la')); }
    finally { setBusyId(null); }
  };
  const goTicket = (it) => navigate(`/alarm-tickets/${it.ticket_id}`);
  const goVehicle = (v) => { if (v.vehicle_id && v.vehicle_id !== '—') navigate(`/vehicles/schematics?vehicle=${v.vehicle_id}`); };

  const TicketActions = ({ it, v }) => (
    <Stack direction="row" spacing={0.25}>
      <Tooltip title={t('hardcoded.pages_tpmsactionsummarypage.ver_ticket')}><IconButton size="small" onClick={() => goTicket(it)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
      {it.status !== 'acknowledged' && (
        <Tooltip title={t('hardcoded.pages_alarmticketdetailpage.marcar_visto')}><span><IconButton size="small" color="warning" disabled={busyId === it.ticket_id} onClick={() => doAck(it)}><SeenIcon fontSize="small" /></IconButton></span></Tooltip>
      )}
      <Tooltip title={t('hardcoded.components_schematics_vehicleticketsdrawer.resolver')}><span><IconButton size="small" color="success" disabled={busyId === it.ticket_id} onClick={() => doResolve(it)}><ResolveIcon fontSize="small" /></IconButton></span></Tooltip>
      <Tooltip title={t('hardcoded.components_tickets_ticketactioncenter.crear_tarea')}><span><IconButton size="small" color="primary" disabled={busyId === it.ticket_id} onClick={() => doCreateTask(it, v)}><AddTaskIcon fontSize="small" /></IconButton></span></Tooltip>
    </Stack>
  );

  return (
    <Box className="aps-print-area" sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Encabezado que solo aparece en papel: empresa, fecha y alcance. */}
      <Box className="aps-print-header">
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Resumen de Acciones — Arsenal APS
        </Typography>
        <Typography variant="body2">
          {currentCompany?.company_name || currentCompany?.name || ''}
        </Typography>
        <Typography variant="caption">Impreso: {printTimestamp(i18n.language || 'es-MX')}</Typography>
      </Box>

      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ActionIcon /> {t('hardcoded.components_common_sidebar.resumen_de_acciones')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('hardcoded.pages_tpmsactionsummarypage.que_esta_pasando_ahora_mismo')}</Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          {generatedAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{i18n.t('hardcoded.components_ai_aimemoryeditor.actualizado')}{generatedAt.toLocaleString(i18n.language || 'es-MX')}</Typography>
          )}
          <Tooltip title={t('audit.actions.UPDATE')}><span><IconButton onClick={load} disabled={loading} color="primary"><RefreshIcon /></IconButton></span></Tooltip>
          <Tooltip title={t('hardcoded.components_fleet.imprimir')}>
            <span>
              <IconButton className="aps-no-print" onClick={printSection} disabled={loading} color="primary">
                <PrintIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><KpiCard label={t('hardcoded.pages_tpmsactionsummarypage.tickets_activos')} value={totals.tickets ?? 0} color="#d32f2f" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard label={t('vehicleTicketsDrawer.openLabel')} value={totals.open ?? 0} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard label={t('vehicleTicketsDrawer.acknowledgedLabel')} value={totals.acknowledged ?? 0} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard label={t('hardcoded.pages_tpmsactionsummarypage.vehiculos_afectados')} value={totals.vehicles_affected ?? 0} /></Grid>
      </Grid>

      {/* Cierre automatico bloqueado: el PSI subio por temperatura, no por calibracion. */}
      {(totals.temperature_blocked ?? 0) > 0 && (
        <Paper variant="outlined" className="aps-print-block"
          sx={{ p: 1.5, mb: 2, borderRadius: 2, borderColor: 'warning.main', bgcolor: 'warning.50' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.dark' }}>
            {totals.temperature_blocked} ticket(s) siguen abiertos pese a que la presion actual parece en rango
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            La presion actual esta en rango por temperatura, pero la presion en frio estimada sigue baja.
            Verificar con manometro y calibrar en frio.
          </Typography>
        </Paper>
      )}

      {/* Severity chips */}
      {(totals.tickets ?? 0) > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
          {['extreme', 'critical', 'alarm'].filter((s) => bySev[s]).map((s) => (
            <Chip key={s} label={`${sevColor(s).label}: ${bySev[s]}`} size="small"
              onClick={() => setFSeverity(fSeverity === s ? 'all' : s)}
              sx={{ bgcolor: sevColor(s).bg, color: '#fff', fontWeight: 700, opacity: fSeverity === 'all' || fSeverity === s ? 1 : 0.5, cursor: 'pointer' }} />
          ))}
          {Object.entries(totals.by_category || {}).map(([cat, n]) => (
            <Chip key={cat} icon={CATEGORY_ICON[cat]} label={`${cat}: ${n}`} size="small" variant="outlined"
              onClick={() => setFCategory(fCategory === cat ? 'all' : cat)}
              sx={{ opacity: fCategory === 'all' || fCategory === cat ? 1 : 0.5, cursor: 'pointer' }} />
          ))}
        </Stack>
      )}

      {/* Filtros rápidos por tipo de incidencia */}
      {(totals.tickets ?? 0) > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
          {QUICK_FILTERS.map((qf) => (
            <Chip key={qf.key} label={qf.label} size="small"
              color={quick === qf.key ? 'primary' : 'default'}
              variant={quick === qf.key ? 'filled' : 'outlined'}
              onClick={() => setQuick(quick === qf.key ? 'all' : qf.key)}
              sx={{ cursor: 'pointer', fontWeight: quick === qf.key ? 700 : 500 }} />
          ))}
        </Stack>
      )}

      {/* Filtros */}
      {(totals.tickets ?? 0) > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4} md={4}>
              <TextField fullWidth size="small" placeholder={t('hardcoded.pages_tpmsactionsummarypage.buscar_vehiculo_o_ticket')} value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2.5}>
              <TextField select fullWidth size="small" label={t('hardcoded.components_settings_notificationpreferenceseditor.severidad')} value={fSeverity} onChange={(e) => setFSeverity(e.target.value)}>
                <MenuItem value="all">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                <MenuItem value="extreme">{t('alarmTickets.severity.extreme')}</MenuItem>
                <MenuItem value="critical">{t('alarmTickets.severity.critical')}</MenuItem>
                <MenuItem value="alarm">{i18n.t('alarmTickets.severity.alarm')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2.5}>
              <TextField select fullWidth size="small" label={t('vehicles.form.type')} value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
                <MenuItem value="all">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                {categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <TextField select fullWidth size="small" label={t('gps.table.status')} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                <MenuItem value="all">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                <MenuItem value="open">{i18n.t('vehicleTicketsDrawer.openLabel')}</MenuItem>
                <MenuItem value="acknowledged">{i18n.t('vehicleTicketsDrawer.acknowledgedLabel')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={12} md={1}>
              <Typography variant="caption" color="text.secondary">{shownCount} {i18n.t('hardcoded.pages_storepage.resultados')}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress color="primary" /></Box>
      )}

      {/* Empty state */}
      {!loading && (totals.tickets ?? 0) === 0 && (
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <OkIcon sx={{ fontSize: 56, color: 'success.main' }} />
          <Typography variant="h6" sx={{ mt: 1, fontWeight: 700 }}>{t('hardcoded.pages_tpmsactionsummarypage.todo_en_orden')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('hardcoded.pages_tpmsactionsummarypage.no_hay_tickets_abiertos_ni')}</Typography>
        </Paper>
      )}

      {!loading && (totals.tickets ?? 0) > 0 && shownCount === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_tpmsactionsummarypage.ningun_ticket_coincide_con_los')}</Typography>
          <Button size="small" onClick={() => { setFSeverity('all'); setFCategory('all'); setFStatus('all'); setSearch(''); }}>{i18n.t('audit.filters.clearFilters')}</Button>
        </Paper>
      )}

      {/* Vehicles */}
      {!loading && vehicles.map((v) => {
        const sc = sevColor(v.max_severity);
        return (
          <Accordion key={v.vehicle_id} defaultExpanded={v.max_severity_rank >= 2}
            elevation={0} sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', flexWrap: 'wrap' }}>
                <Avatar sx={{ bgcolor: sc.bg, width: 34, height: 34 }}><CarIcon fontSize="small" /></Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 120 }}>
                  <Typography sx={{ fontWeight: 700 }}>{v.vehicle_name}</Typography>
                  {v.configuration && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('hardcoded.pages_tpmsactionsummarypage.config')}{v.configuration}</Typography>
                  )}
                </Box>
                <Chip size="small" label={v.max_severity_label} sx={{ bgcolor: sc.bg, color: '#fff', fontWeight: 700 }} />
                <Chip size="small" variant="outlined" label={`${v.items.length} ${v.items.length === 1 ? t('hardcoded.components_ai_aientitycards.ticket') : t('hardcoded.components_common_sidebar.tickets')}`} />
                {v.vehicle_id && v.vehicle_id !== '—' && (
                  <Tooltip title={t('hardcoded.components_tickets_ticketaianalysiscard.ver_vehiculo')}>
                    <IconButton size="small" component="span" onClick={(e) => { e.stopPropagation(); goVehicle(v); }}><OpenIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  {v.positions_count ? (
                    <VehicleSchematic
                      vehicle={{ figure: v.figure, positions_count: v.positions_count, vehicle_sensors: [] }}
                      t={tFallback}
                      selectedPositions={v.highlight_positions || []}
                      compact
                    />
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="caption">{t('hardcoded.pages_tpmsactionsummarypage.sin_esquematico_disponible')}</Typography>
                    </Paper>
                  )}
                  <PressuresPanel items={v.items} />
                  <FleetPlanPanel plan={v.action_plan} highlight={v.highlight_positions || []} />
                </Grid>

                <Grid item xs={12} md={8}>
                  {isMobile ? (
                    <Stack divider={<Divider />}>
                      {v.items.map((it) => (
                        <Box key={it.ticket_id} sx={{ p: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                            <Chip size="small" label={it.severity_label} sx={{ bgcolor: sevColor(it.severity).bg, color: '#fff', height: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.metric_label}</Typography>
                            <Typography variant="caption" color="text.secondary">· {it.location_label}</Typography>
                          </Stack>
                          <Typography variant="body2">{it.value_label}{it.range_label !== '—' ? ` (${t('hardcoded.pages_tpmsactionsummarypage.rango')} ${it.range_label})` : ''}</Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: 'primary.main' }}>➤ {it.action}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>#{it.ticket_number} · {it.status === 'acknowledged' ? t('alarmTickets.status.acknowledged') : t('alarmTickets.status.open')} · {it.age_label}</Typography>
                          <ResolutionBlockNote block={it.resolution_block} />
                          <TicketActions it={it} v={v} />
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', bgcolor: '#fafafa' } }}>
                          <TableCell>{t('hardcoded.components_settings_notificationpreferenceseditor.severidad')}</TableCell>
                          <TableCell>{t('hardcoded.components_admin_ordermanagement.ubicacion')}</TableCell>
                          <TableCell>{i18n.t('hardcoded.pages_tpmsactionsummarypage.lectura')}</TableCell>
                          <TableCell>{t('hardcoded.pages_tpmsactionsummarypage.que_hay_que_hacer')}</TableCell>
                          <TableCell align="right">{i18n.t('hardcoded.components_alarmprofiles_profileform.tiempo')}</TableCell>
                          <TableCell align="right">{t('common.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {v.items.map((it) => (
                          <TableRow key={it.ticket_id} hover>
                            <TableCell>
                              <Chip size="small" label={it.severity_label} sx={{ bgcolor: sevColor(it.severity).bg, color: '#fff', fontWeight: 700, height: 22 }} />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {CATEGORY_ICON[it.category]}
                                <span>{it.location_label}</span>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <strong>{it.metric_label}</strong><br />
                              <Typography variant="caption" color="text.secondary">{it.value_label}{it.range_label !== '—' ? ` · ${it.range_label}` : ''}</Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 340 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.action}</Typography>
                              <Typography variant="caption" color="text.secondary">#{it.ticket_number} · {it.status === 'acknowledged' ? t('alarmTickets.status.acknowledged') : t('alarmTickets.status.open')}</Typography>
                              <ResolutionBlockNote block={it.resolution_block} />
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" variant="outlined" color={it.age_hours >= 48 ? 'error' : 'default'} label={it.age_label} />
                            </TableCell>
                            <TableCell align="right"><TicketActions it={it} v={v} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default TPMSActionSummaryPage;
